/**
 * FinancialTimeline.jsx — Wave F3-D detail page.
 *
 * A rich, audit-grade view of one Financial Lifecycle:
 *   - Health Score ring (Timeline Integrity).
 *   - Per-phase status badges + collapsible event lists.
 *   - Per-event duration + cumulative time + SLA color.
 *   - Anomaly panel (missing / out-of-sequence events).
 *   - Reversal banner when a journal was reversed.
 *   - Deep-linked navigation to the underlying entity.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Activity, ArrowLeft, ChevronDown, ChevronUp, Clock, ShieldCheck,
  ShieldAlert, AlertTriangle, XCircle, ExternalLink, RotateCcw,
  Upload, ScanText, CheckCheck, FileText, FilePenLine, ShieldCheck as ShieldOK,
  CircleDollarSign, Wallet, Landmark, Sparkles,
} from 'lucide-react';


const PHASE_META = {
  document:       { label: 'المُستَنَد',     icon: Upload,          color: 'text-slate-700' },
  approval:       { label: 'الاعتِماد',      icon: CheckCheck,      color: 'text-amber-700' },
  bill:           { label: 'الفاتورَة',      icon: FileText,        color: 'text-indigo-700' },
  journal:        { label: 'القَيد',          icon: FilePenLine,     color: 'text-blue-700' },
  payment:        { label: 'الدَّفع',          icon: CircleDollarSign,color: 'text-emerald-700' },
  reconciliation: { label: 'المُطابَقَة',     icon: Landmark,        color: 'text-teal-700' },
};

const EVENT_LABEL_AR = {
  'document.uploaded':       'تَم رَفع المُستَنَد',
  'document.classified':     'تَصنيف المُستَنَد',
  'document.extracted':      'استِخراج البَيانات (OCR)',
  'document.corrected':      'تَصحيح يَدَوي',
  'approval.submitted':      'إرسال لِلاعتِماد',
  'approval.approved':       'تَم الاعتِماد',
  'approval.rejected':       'رَفض الاعتِماد',
  'bill.drafted':            'إنشاء مُسَوَّدَة فاتورَة',
  'bill.coded':              'تَرميز مُحاسَبي',
  'bill.verified':           'تَحقُّق من الفاتورَة',
  'journal.preview_created': 'مُعايَنَة القَيد',
  'journal.preview_stale':   'مُعايَنَة قَديمَة',
  'journal.posted':          'تَرحيل القَيد',
  'journal.reversed':        'عَكس القَيد',
  'vat.recorded':            'تَسجيل ضَريبَة القيمَة المُضافَة',
  'payment.ready':           'جاهِز لِلدَّفع',
  'payment.run_created':     'إنشاء دُفعَة دَفع',
  'payment.run_approved':    'اعتِماد دُفعَة الدَّفع',
  'payment.voucher_created': 'إنشاء سَنَد صَرف',
  'payment.voucher_approved':'اعتِماد سَنَد الصَّرف',
  'payment.sent_to_bank':    'إرسال إلى البَنك',
  'payment.paid':            'تَم الدَّفع',
  'payment.reconciled':      'مُطابَقَة بَنكيَّة',
};

const SLA_COLORS = {
  green: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
  red:   'bg-red-100 text-red-700 border-red-200',
  'n/a': 'bg-slate-100 text-slate-500 border-slate-200',
};

const PHASE_STATUS_STYLE = {
  green:   'border-emerald-500 bg-emerald-50',
  amber:   'border-amber-500 bg-amber-50',
  red:     'border-red-500 bg-red-50',
  pending: 'border-slate-300 bg-slate-50',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ar-SA', {
      hour12: false, year: 'numeric', month: 'short',
      day: 'numeric', hour: '2-digit', minute: '2-digit',
      second: '2-digit',
    });
  } catch { return iso.slice(0, 19); }
}


export default function FinancialTimelinePage() {
  const { flId } = useParams();
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openPhase, setOpenPhase] = useState({});
  const [expandedEvent, setExpandedEvent] = useState({});

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await axiosInstance.get(
        `/financial-timeline/${flId}`);
      setData(data);
      // Auto-expand phases with events on first load.
      const map = {};
      (data.phases || []).forEach((p) => {
        map[p.phase] = (p.events || []).length > 0;
      });
      setOpenPhase(map);
    } catch (e) {
      setError(e?.response?.data?.detail || 'فَشَل تَحميل الخَط الزَّمَني');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [flId]);

  if (loading) return <div className="p-8 text-slate-500">جاري التَّحميل…</div>;
  if (error)   return <div className="p-8 text-red-600">
    <XCircle className="inline w-5 h-5" /> {error}
  </div>;
  if (!data)   return null;

  const health = data.health_score || 0;
  const healthColor =
    health >= 90 ? 'text-emerald-600' :
    health >= 70 ? 'text-blue-600' :
    health >= 40 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="fintl-detail-root">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <Link to="/financial-timelines"
                className="text-sm text-slate-500 hover:underline flex items-center gap-1 mb-2"
                data-testid="fintl-back-btn">
            <ArrowLeft className="w-4 h-4" /> عودَة إلى القائِمَة
          </Link>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Activity className="w-8 h-8 text-blue-600" />
            <span className="font-mono">{data.fl_id}</span>
          </h1>
          <div className="text-slate-600 mt-1 text-sm flex items-center gap-4">
            <span><Clock className="inline w-4 h-4" />{' '}
              أَوَّل حَدَث: {fmtDate(data.summary?.first_event_at)}</span>
            <span>·</span>
            <span>آخِر حَدَث: {fmtDate(data.summary?.last_event_at)}</span>
            {data.durations?.total_label && (
              <>
                <span>·</span>
                <span data-testid="fintl-total-duration">
                  المُدَّة الكُلية: <b>{data.durations.total_label}</b>
                </span>
              </>
            )}
          </div>
        </div>

        {/* Health Ring */}
        <div className="bg-white border rounded-xl p-4 text-center min-w-[140px]"
              data-testid="fintl-health-card">
          <div className="text-xs text-slate-500 mb-1 flex items-center justify-center gap-1">
            <ShieldCheck className="w-3 h-3" /> سَلامَة الخَط الزَّمَني
          </div>
          <div className={`text-4xl font-bold ${healthColor}`}
                data-testid="fintl-health-score">
            {health}%
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {data.integrity?.anomaly_count || 0} مُلاحَظَة
          </div>
        </div>
      </div>

      {/* Reversal Banner */}
      {data.reversal?.is_reversed && (
        <div className="mb-6 border-2 border-amber-300 bg-amber-50 rounded-lg p-4"
              data-testid="fintl-reversal-banner">
          <div className="flex items-start gap-3">
            <RotateCcw className="w-6 h-6 text-amber-600 mt-1" />
            <div>
              <div className="font-bold text-amber-900">قَيد مَعكوس</div>
              <div className="text-sm text-amber-800 mt-1">
                تَم عَكس القَيد بِتاريخ {fmtDate(data.reversal.reversed_at)}.
                {data.reversal.reason && (
                  <span className="block mt-1">
                    <b>السَّبَب:</b> {data.reversal.reason}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Anomalies */}
      {(data.integrity?.anomalies || []).length > 0 && (
        <div className="mb-6 border rounded-lg overflow-hidden"
              data-testid="fintl-anomalies-panel">
          <div className="bg-red-50 border-b border-red-200 px-4 py-2 text-red-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <span className="font-semibold">مُلاحَظات نَزاهَة</span>
            <span className="text-xs bg-red-200 rounded px-2 py-0.5">
              {data.integrity.anomalies.length}
            </span>
          </div>
          <div className="divide-y">
            {data.integrity.anomalies.map((a, i) => (
              <div key={i} className="px-4 py-2 text-sm flex items-center gap-2"
                    data-testid={`fintl-anomaly-${i}`}>
                {a.severity === 'error'
                  ? <XCircle className="w-4 h-4 text-red-500" />
                  : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                <span className="text-slate-700">{a.message}</span>
                <span className="text-xs text-slate-400 mr-auto font-mono">
                  {a.event_type}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phases */}
      <div className="space-y-4">
        {(data.phases || []).map((phase) => {
          const meta = PHASE_META[phase.phase] || {};
          const Icon = meta.icon || Sparkles;
          const isOpen = openPhase[phase.phase];
          const isEmpty = (phase.events || []).length === 0;
          const style = PHASE_STATUS_STYLE[isEmpty ? 'pending' : phase.status];
          return (
            <div key={phase.phase}
                  data-testid={`fintl-phase-${phase.phase}`}
                  className={`border-2 rounded-xl overflow-hidden ${style}`}>
              <button
                data-testid={`fintl-phase-toggle-${phase.phase}`}
                onClick={() => setOpenPhase({...openPhase, [phase.phase]: !isOpen})}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/50 transition"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center
                                    justify-center bg-white ${meta.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-slate-900">
                      {phase.label_ar}
                    </div>
                    <div className="text-xs text-slate-500">
                      {isEmpty ? 'لا أَحداث بَعد'
                        : `${phase.events.length} حَدَث · مُدَّة: ${phase.duration_label || '—'}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!isEmpty && (
                    <span className={`text-xs px-2 py-1 rounded-full border ${SLA_COLORS[phase.status] || SLA_COLORS['n/a']}`}>
                      {phase.status === 'green' ? 'ضِمن SLA'
                        : phase.status === 'amber' ? 'قَريب مِن التَجاوُز'
                        : phase.status === 'red' ? 'تَجاوَز SLA'
                        : '—'}
                    </span>
                  )}
                  {isOpen ? <ChevronUp className="w-5 h-5 text-slate-500" />
                          : <ChevronDown className="w-5 h-5 text-slate-500" />}
                </div>
              </button>
              {isOpen && !isEmpty && (
                <div className="bg-white border-t divide-y">
                  {phase.events.map((ev) => (
                    <EventRow
                      key={ev.event_id}
                      ev={ev}
                      expanded={!!expandedEvent[ev.event_id]}
                      onToggle={() => setExpandedEvent({
                        ...expandedEvent,
                        [ev.event_id]: !expandedEvent[ev.event_id],
                      })}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function EventRow({ ev, expanded, onToggle }) {
  const label = EVENT_LABEL_AR[ev.event_type] || ev.event_type;
  const isReversal = ev.event_type === 'journal.reversed';
  const bg = isReversal ? 'bg-amber-50' : '';
  return (
    <div className={`px-5 py-3 hover:bg-slate-50 ${bg}`}
          data-testid={`fintl-event-${ev.event_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900">{label}</span>
            {ev.sla_status && ev.sla_status !== 'n/a' && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${SLA_COLORS[ev.sla_status]}`}
                    data-testid={`fintl-event-sla-${ev.event_id}`}>
                {ev.sla_status === 'green' ? '🟢 SLA'
                  : ev.sla_status === 'amber' ? '🟡 قَريب'
                  : '🔴 تَجاوُز'}
              </span>
            )}
            {isReversal && (
              <span className="text-[10px] px-2 py-0.5 rounded-full
                                bg-amber-100 text-amber-700 border border-amber-200">
                <RotateCcw className="inline w-3 h-3" /> عَكس
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
            <span>⏱ {fmtDate(ev.timestamp)}</span>
            {ev.delta_label && (
              <span>Δ {ev.delta_label}</span>
            )}
            {ev.cumulative_label && (
              <span>مُجَمَّع: {ev.cumulative_label}</span>
            )}
            {ev.actor?.name && (
              <span>👤 {ev.actor.name}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {ev.nav?.route && (
            <Link
              to={ev.nav.route}
              data-testid={`fintl-event-nav-${ev.event_id}`}
              className="text-xs text-blue-700 hover:underline flex items-center gap-1"
            >
              {ev.nav.label} <ExternalLink className="w-3 h-3" />
            </Link>
          )}
          <button
            data-testid={`fintl-event-toggle-${ev.event_id}`}
            onClick={onToggle}
            className="text-xs text-slate-500 hover:text-slate-800"
          >
            {expanded ? 'إخفاء' : 'تَفاصيل'}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 bg-slate-50 rounded-lg p-3 text-xs font-mono text-slate-700 overflow-x-auto"
              data-testid={`fintl-event-details-${ev.event_id}`}>
          <div><b>event_id:</b> {ev.event_id}</div>
          <div><b>event_type:</b> {ev.event_type}</div>
          <div><b>entity:</b> {ev.entity_type} / {ev.entity_id}</div>
          <div><b>source_service:</b> {ev.source_service}</div>
          <div><b>correlation_id:</b> {ev.correlation_id}</div>
          {ev.sla_target_seconds != null && (
            <div><b>sla_target:</b> {ev.sla_target_seconds}s</div>
          )}
          {ev.metadata && Object.keys(ev.metadata).length > 0 && (
            <pre className="mt-2 whitespace-pre-wrap">
              {JSON.stringify(ev.metadata, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
